import classNames from 'classnames';
import React, { useEffect, useRef } from 'react';
import ContentEditable from 'react-contenteditable';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';

type ContentEditableInputProps = {
  value: string;
  onChange: (value: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  setFocusRef?: React.MutableRefObject<() => void>;
  setScrollToTopRef?: React.MutableRefObject<() => void>;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onDoubleClick?: () => void;
  autofocus?: boolean;
  onPaste?: (e: React.ClipboardEvent) => void;
  maxLength?: number;
};

export const ContentEditableInput = ({
  value,
  onChange,
  onEditStart,
  onEditEnd,
  onKeyDown,
  onKeyPress,
  setFocusRef,
  setScrollToTopRef,
  className,
  disabled,
  placeholder,
  onDoubleClick,
  autofocus = false,
  onPaste,
  maxLength,
}: ContentEditableInputProps) => {
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!setFocusRef) {
      return;
    }
    setFocusRef.current = () => {
      if (!ref.current) {
        return;
      }

      if (window.getSelection && document.createRange) {
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        const sel = window.getSelection();
        if (sel) {
          sel.removeAllRanges();
          sel.addRange(range);
        }
      }
      ref.current.focus();
    };
  }, [setFocusRef]);

  useEffect(() => {
    if (!setScrollToTopRef) {
      return;
    }
    setScrollToTopRef.current = () => {
      if (!ref.current) {
        return;
      }

      const el = ref.current;
      el.style.overflow = 'scroll';
      el.scrollTop = 0;
      el.scrollLeft = 0;
      el.style.overflow = 'hidden';
    };
  }, [setScrollToTopRef]);

  useEffect(() => {
    if (autofocus) {
      ref.current?.focus();
    }
  }, []);

  const handleChange = useRefCallback(
    (evt) => {
      let newValue = evt.target.value;

      if (maxLength !== undefined && newValue.length > maxLength) {
        newValue = newValue.substring(0, maxLength);
      }

      onChange(newValue);
    },
    [onChange, maxLength]
  );

  const handleBlur = useRefCallback(() => {
    onEditEnd && onEditEnd();
  }, [onEditEnd]);

  const handleFocus = useRefCallback(() => {
    onEditStart && onEditStart();
  }, [onEditStart]);

  const handleKeyDown = useRefCallback(
    (evt) => {
      if ((!evt.shiftKey && evt.key === 'Enter') || evt.key === 'Escape') {
        evt.preventDefault();
        ref.current?.blur();
      }
      onKeyDown && onKeyDown(evt);
    },
    [ref, onKeyDown]
  );

  const handleKeyPress = useRefCallback((evt) => {
    onKeyPress && onKeyPress(evt);
  });

  const handlePaste = useRefCallback(
    (evt) => {
      onPaste && onPaste(evt);
    },
    [onPaste]
  );

  const handleMouseDown = useRefCallback((evt) => {
    evt.stopPropagation();
  }, []);

  return (
    <ContentEditable
      disabled={disabled}
      innerRef={ref}
      className={classNames(className, 'neue-contenteditable')}
      html={value}
      onFocus={handleFocus}
      onPaste={handlePaste}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyPress={handleKeyPress}
      onDoubleClick={onDoubleClick}
      onMouseDown={handleMouseDown}
      placeholder={placeholder}
    />
  );
};
